import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _omit from "lodash/omit";
import _compact from "lodash/compact";
var _excluded = ["analytics", "children", "label", "title"];
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { usePopoverState, Popover, PopoverDisclosure, usePopoverArrow } from 'reakit/Popover';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useId } from '@nerdwallet/react-accessibility';
import Help from '@nerdwallet/react-icon/nerdwallet-ui/Help';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { Text } from '../Text/Text';
import { useElementInteraction } from './useElementInteraction';
import styles from './Tooltip.module.less';
export var Tooltip = function Tooltip(_ref) {
  var analytics = _ref.analytics,
      ariaLabel = _ref['aria-label'],
      children = _ref.children,
      className = _ref.className,
      content = _ref.content,
      dangerouslyForceFocusStyling = _ref.dangerouslyForceFocusStyling,
      idProp = _ref.id,
      _ref$initiallyOpen = _ref.initiallyOpen,
      initiallyOpen = _ref$initiallyOpen === void 0 ? false : _ref$initiallyOpen,
      label = _ref.label,
      htmlOnBlur = _ref.onBlur,
      onChangeContentVisibility = _ref.onChangeContentVisibility,
      htmlOnClick = _ref.onClick,
      htmlOnFocus = _ref.onFocus,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      style = _ref.style,
      htmlTitle = _ref.title,
      popperOptions = _ref.popperOptions;
  // TODO: remove in Currency v3
  useDeprecatedPropsWarning({
    component: 'Tooltip',
    deprecatedProps: [{
      deprecated: 'popperOptions',
      migration: 'Custom icons are no longer recommended for use in designs.',
      version: '1.3.0'
    }],
    props: {
      popperOptions: popperOptions
    }
  });
  useDeprecatedClassNameStylePropsWarning('Tooltip', {
    className: className,
    style: style
  });
  var id = useId(idProp);
  var isTextTooltip = typeof content === 'string';
  var state = usePopoverState({
    baseId: id,
    placement: placement,
    // animate: true // TODO: set this to true if using css animations/transitions
    visible: initiallyOpen
  });
  var arrowState = usePopoverArrow(state);
  var visible = state.visible,
      animating = state.animating; // determine if content is visible (i.e. potential animations are completed)

  var contentVisible = visible ? !animating : animating; // call visibility changed handler when content visibility changes

  var _useState = useState(contentVisible),
      _useState2 = _slicedToArray(_useState, 2),
      prevContentIsVisible = _useState2[0],
      setPrevContentIsVisible = _useState2[1];

  useEffect(function () {
    if (contentVisible === prevContentIsVisible) return undefined;
    setPrevContentIsVisible(contentVisible);
    onChangeContentVisibility === null || onChangeContentVisibility === void 0 ? void 0 : onChangeContentVisibility(contentVisible);
    return undefined;
  }, [contentVisible, prevContentIsVisible, onChangeContentVisibility]); // build handlers for firing bedrock events after interactions

  var elementInteractionForTrigger = useElementInteraction({
    // truthiness of analytics props determines if events are fired
    enable: !!analytics,
    entityName: _typeof(analytics) === 'object' && (analytics === null || analytics === void 0 ? void 0 : analytics.entityName) || _compact([_typeof(analytics) === 'object' && (analytics === null || analytics === void 0 ? void 0 : analytics.entityNamePrefix), id]).join('_'),
    // visibility can be delayed by library
    // so using this hook here in tooltip means
    // it needs the reakit visible state and the reakit animating state
    visible: contentVisible,
    eventType: _typeof(analytics) === 'object' ? analytics === null || analytics === void 0 ? void 0 : analytics.eventType : undefined,
    eventPayload: _typeof(analytics) === 'object' ? analytics === null || analytics === void 0 ? void 0 : analytics.payload : undefined,
    // chain event handlers from props if they exist.
    onClick: htmlOnClick,
    onFocus: htmlOnFocus,
    onBlur: htmlOnBlur
  }); // add a Text wrapper if label is a string

  var isTextLabel = typeof label === 'string'; // create a title prop for the trigger

  var triggerTitle = htmlTitle || ariaLabel;

  if (!triggerTitle && label && isTextLabel) {
    triggerTitle = "More information about ".concat(label);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, label && isTextLabel ? /*#__PURE__*/React.createElement(Text, null, label) : label, /*#__PURE__*/React.createElement(PopoverDisclosure, _extends({}, state, {
    as: "button",
    type: "button",
    "data-currency": "PopoverDisclosure" // specific styles added with className prop
    ,
    className: classNames(styles.trigger, className, _defineProperty({}, styles['force-focus'], dangerouslyForceFocusStyling)) // event handlers for element interactions

  }, elementInteractionForTrigger, {
    // generated title
    title: triggerTitle
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.tappable,
    "aria-hidden": "true"
  }), children || /*#__PURE__*/React.createElement(Help, null)), /*#__PURE__*/React.createElement(Popover, _extends({}, state, {
    // use a span instead of a div to prevent `cannot nest <div> inside of <p>` DOM validation errors
    as: "span",
    className: classNames(styles.tooltip, _defineProperty({}, styles.textTooltip, isTextTooltip)) // tabIndex=0 transfers focus from trigger to popover dialog
    // when it is opened. this allows further tab-navigating
    // into the content of the tooltip (links, buttons, etc...)
    ,
    tabIndex: 0 // first thing screen reader reads, so
    // e.g. "<this was the label prop>, dialog, 4 items..."
    ,
    "aria-label": ariaLabel || htmlTitle || (isTextLabel ? label : undefined) // reakit sets role of dialog

  }), /*#__PURE__*/React.createElement("span", _extends({}, _omit(arrowState, 'children'), {
    className: classNames(styles.arrow, styles["arrow-".concat(state.placement)] // top, bottom, right, left
    ) // don't count this in element count of dialog when screen reader is reading
    ,
    "aria-hidden": true
  })), isTextTooltip ? /*#__PURE__*/React.createElement(Text, {
    size: "default"
  }, content) : content));
};
Tooltip.displayName = 'Tooltip';

/**
 * HelpBubble is a simple wrapper that never prints the label
 * also, it adds analytics prefix to keep compatibility with legacy @nerdwallet/react-help-bubble
 */
export var HelpBubble = function HelpBubble(_ref2) {
  var analyticsProp = _ref2.analytics,
      children = _ref2.children,
      label = _ref2.label,
      title = _ref2.title,
      props = _objectWithoutProperties(_ref2, _excluded);

  var analytics;

  if (typeof analyticsProp === 'boolean') {
    if (analyticsProp) {
      analytics = {
        entityNamePrefix: 'help_bubble'
      };
    } else {
      analytics = false;
    }
  } else if (_typeof(analyticsProp) === 'object') {
    analytics = _objectSpread({
      entityNamePrefix: 'help_bubble'
    }, analyticsProp);
  }

  return /*#__PURE__*/React.createElement(Tooltip, _extends({}, props, {
    title: title || (typeof label === 'string' ? label : undefined),
    analytics: analytics
  }), /*#__PURE__*/React.createElement(Help, null));
};